import { useState, useEffect } from 'react';
import { useSearch } from 'wouter';
import { useListGames, useListSports } from '@workspace/api-client-react';
import { Navbar } from '@/components/Navbar';
import { GameCard } from '@/components/GameCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Search, Gamepad2, Terminal } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { difficultyFor, TIER_META, type DifficultyTier } from '@/lib/difficulty';

const TIERS: DifficultyTier[] = ['rookie', 'pro', 'allstar'];

export const GAME_TYPES = [
  { key: 'logo-reveal', name: 'Logo Reveal' },
  { key: 'face-zoom', name: 'Face Zoom' },
  { key: 'player-reveal', name: 'Player Reveal' },
  { key: 'stadium-guess', name: 'Stadiums' },
  { key: 'higher-lower', name: 'Founded First' },
  { key: 'jersey-number', name: 'Jersey Numbers' },
  { key: 'team-roster', name: 'Roster Check' },
  { key: 'player-older', name: 'Age Check' },
  { key: 'stat-duel', name: 'Stat Showdown' },
];

export default function Games() {
  // wouter's useLocation() strips the query string — useSearch() is required
  // for ?sport=... links (e.g. from the homepage) to actually apply.
  const searchString = useSearch();
  const params = new URLSearchParams(searchString);

  const [search, setSearch] = useState(params.get('search') || '');
  const [sport, setSport] = useState(params.get('sport') || '');
  const [gameType, setGameType] = useState(params.get('type') || '');
  const [sort, setSort] = useState<'trending' | 'new' | 'popular'>((params.get('sort') as any) || 'trending');
  const [difficulty, setDifficulty] = useState<DifficultyTier | ''>(
    TIERS.includes(params.get('difficulty') as DifficultyTier) ? (params.get('difficulty') as DifficultyTier) : '',
  );

  // Mirror URL state fully: absent params reset to defaults so stale
  // filters don't survive navigation between /games URLs.
  useEffect(() => {
    const p = new URLSearchParams(searchString);
    setSport(p.get('sport') || '');
    setGameType(p.get('type') || '');
    setSearch(p.get('search') || '');
    const s = p.get('sort');
    setSort(s === 'new' || s === 'popular' ? s : 'trending');
    const d = p.get('difficulty') as DifficultyTier;
    setDifficulty(TIERS.includes(d) ? d : '');
  }, [searchString]);

  const { data: sports } = useListSports();
  const { data: games, isLoading } = useListGames({ sport: sport || undefined, type: gameType || undefined, sort, search: search || undefined });
  // Unfiltered catalog size for the "N games and counting" headline.
  const { data: allGames } = useListGames({});
  const totalGames = allGames?.length;
  // Difficulty is derived from game type on the client, so filter here.
  const visibleGames = difficulty ? games?.filter((g) => difficultyFor(g.gameType).tier === difficulty) : games;

  return (
    <div className="min-h-[100dvh] bg-background">
      <Navbar />

      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="mb-12 animate-slide-up flex flex-col md:flex-row md:items-end justify-between gap-6 border-b-2 border-primary/20 pb-6 relative">
          <div className="absolute bottom-[-2px] left-0 w-32 h-[2px] bg-primary glow-primary" />
          <div>
            <h1 className="mb-2 flex items-center gap-4 text-5xl font-black tracking-tighter uppercase font-display text-shadow-neon-cyan text-primary">
              <Gamepad2 className="h-10 w-10 text-primary animate-pulse" />
              Game Library
            </h1>
            <p className="text-xl font-mono text-muted-foreground uppercase tracking-widest mt-4">
              {totalGames ?? games?.length ?? 0} games and counting
            </p>
          </div>
          
          {/* Filters */}
          <div className="flex-1 max-w-md w-full">
            <div className="relative group">
              <div className="absolute inset-0 bg-primary/20 blur-md rounded-none group-focus-within:bg-primary/40 transition-all duration-500" />
              <div className="relative flex items-center bg-background border border-primary/50 focus-within:border-primary glow-primary transition-all">
                <Terminal className="ml-3 h-5 w-5 text-primary" />
                <Input
                  type="search"
                  placeholder="Search games..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-3 h-12 font-mono uppercase tracking-widest bg-transparent border-none focus-visible:ring-0 placeholder:text-muted-foreground"
                  data-testid="input-search"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="mb-10 space-y-6 animate-slide-up" style={{animationDelay: '0.1s'}}>
          <div className="flex flex-col sm:flex-row gap-6">
            <Tabs value={sort} onValueChange={(v) => setSort(v as any)} className="w-full sm:w-auto">
              <TabsList className="bg-background/50 border border-primary/30 rounded-none h-12 w-full sm:w-auto p-1 gap-1">
                <TabsTrigger value="trending" className="rounded-none font-bold uppercase tracking-widest data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:glow-primary" data-testid="tab-sort-trending">Trending</TabsTrigger>
                <TabsTrigger value="new" className="rounded-none font-bold uppercase tracking-widest data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:glow-primary" data-testid="tab-sort-new">Newest</TabsTrigger>
                <TabsTrigger value="popular" className="rounded-none font-bold uppercase tracking-widest data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:glow-primary" data-testid="tab-sort-popular">Top Rated</TabsTrigger>
              </TabsList>
            </Tabs>

            <div className="flex flex-wrap gap-2 flex-1 items-center">
              <Button
                variant={sport === '' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSport('')}
                className={`rounded-none font-bold uppercase tracking-widest h-10 ${sport === '' ? 'bg-secondary text-secondary-foreground glow-secondary border-secondary' : 'border-secondary/30 text-secondary hover:bg-secondary/10 hover:border-secondary'}`}
                data-testid="filter-sport-all"
              >
                All Sports
              </Button>
              {sports?.map((s) => (
                <Button
                  key={s.key}
                  variant={sport === s.key ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSport(s.key)}
                  className={`rounded-none font-bold uppercase tracking-widest h-10 ${sport === s.key ? 'bg-secondary text-secondary-foreground glow-secondary border-secondary' : 'border-secondary/30 text-secondary hover:bg-secondary/10 hover:border-secondary'}`}
                  data-testid={`filter-sport-${s.key}`}
                >
                  {s.name} <span className="ml-1 opacity-50 font-mono text-[10px]">{s.gameCount}</span>
                </Button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap gap-2 items-center">
            <Button
              variant={gameType === '' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setGameType('')}
              className={`rounded-none font-bold uppercase tracking-widest h-10 ${gameType === '' ? 'bg-primary text-primary-foreground glow-primary border-primary' : 'border-primary/30 text-primary hover:bg-primary/10 hover:border-primary'}`}
              data-testid="filter-type-all"
            >
              All Game Types
            </Button>
            {GAME_TYPES.map((t) => (
              <Button
                key={t.key}
                variant={gameType === t.key ? 'default' : 'outline'}
                size="sm"
                onClick={() => setGameType(t.key)}
                className={`rounded-none font-bold uppercase tracking-widest h-10 ${gameType === t.key ? 'bg-primary text-primary-foreground glow-primary border-primary' : 'border-primary/30 text-primary hover:bg-primary/10 hover:border-primary'}`}
                data-testid={`filter-type-${t.key}`}
              >
                {t.name}
              </Button>
            ))}
          </div>

          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground mr-1">Difficulty</span>
            <Button
              variant={difficulty === '' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setDifficulty('')}
              className={`rounded-none font-bold uppercase tracking-widest h-10 ${difficulty === '' ? 'bg-primary text-primary-foreground glow-primary border-primary' : 'border-primary/30 text-primary hover:bg-primary/10 hover:border-primary'}`}
              data-testid="filter-difficulty-all"
            >
              All Levels
            </Button>
            {TIERS.map((tier) => (
              <Button
                key={tier}
                variant={difficulty === tier ? 'default' : 'outline'}
                size="sm"
                onClick={() => setDifficulty(tier)}
                className={`rounded-none font-bold uppercase tracking-widest h-10 ${difficulty === tier ? 'bg-primary text-primary-foreground glow-primary border-primary' : 'border-primary/30 text-primary hover:bg-primary/10 hover:border-primary'}`}
                data-testid={`filter-difficulty-${tier}`}
              >
                {TIER_META[tier].label}
                <span className="ml-1 opacity-50 font-mono text-[10px]">
                  {games?.filter((g) => difficultyFor(g.gameType).tier === tier).length ?? ''}
                </span>
              </Button>
            ))}
          </div>
        </div>

        {/* Games Grid */}
        {isLoading ? (
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {[...Array(8)].map((_, i) => (
              <Skeleton key={i} className="h-80 rounded-none bg-muted/50 border border-primary/20" />
            ))}
          </div>
        ) : visibleGames && visibleGames.length > 0 ? (
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 stagger-children">
            {visibleGames.map((game) => (
              <GameCard key={game.slug} game={game} />
            ))}
          </div>
        ) : (
          <div className="rounded-none border-2 border-dashed border-primary/30 bg-primary/5 p-16 text-center animate-slide-up">
            <p className="text-xl font-mono text-primary animate-pulse mb-2">
              NO GAMES MATCH THESE FILTERS
            </p>
            <p className="mt-2 text-sm text-muted-foreground font-mono uppercase tracking-widest">
              Adjust filters to continue
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
